import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Plus, Minus, Search } from 'lucide-react';
import { Promotion, PromotionFormData, PromotionType, Product, Location } from '../types';

interface PromotionFormProps {
  promotion?: Promotion | null;
  onSubmit: (data: PromotionFormData) => void;
  onCancel: () => void;
  loading?: boolean;
}

export const PromotionForm: React.FC<PromotionFormProps> = ({
  promotion,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState<PromotionFormData>({
    name: '',
    description: '',
    type: 'percentage',
    value: 0,
    applicableProducts: [],
    applicableLocations: [],
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    daysOfWeek: []
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<number[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [locations, setLocations] = useState<Location[]>([]);

  // Mock products - in real app, this would come from an API
  const mockProducts: Product[] = [
    { id: '1', name: 'Hamburguesa Clásica', price: 18.50, category: 'Hamburguesas' },
    { id: '2', name: 'Cheeseburger', price: 20.00, category: 'Hamburguesas' },
    { id: '3', name: 'Sandwich de Pollo', price: 15.50, category: 'Sandwiches' },
    { id: '4', name: 'Sandwich Italiano', price: 16.00, category: 'Sandwiches' },
    { id: '5', name: 'Papas Fritas', price: 8.00, category: 'Acompañamientos' },
    { id: '6', name: 'Onion Rings', price: 9.50, category: 'Acompañamientos' },
    { id: '7', name: 'Coca Cola', price: 5.00, category: 'Bebidas' },
    { id: '8', name: 'Inca Kola', price: 5.00, category: 'Bebidas' },
    { id: '9', name: 'Combo Familiar', price: 45.00, category: 'Combos' },
    { id: '10', name: 'Combo Personal', price: 25.00, category: 'Combos' }
  ];

  const filteredProducts = mockProducts.filter(product => 
    product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    product.category.toLowerCase().includes(productSearch.toLowerCase())
  );

  // Load locations on component mount
  useEffect(() => {
    const loadLocations = async () => {
      try {
        // Mock locations for now - in real app, fetch from API
        const mockLocations: Location[] = [
          { id: 1, nombre: 'Macartur Centro', tipoLocal: 'PRINCIPAL', direccion: 'Av. Principal 123', isActive: true },
          { id: 2, nombre: 'Macartur 2', tipoLocal: 'SUCURSAL', direccion: 'Av. Secundaria 456', isActive: true },
          { id: 3, nombre: 'Macartur 3', tipoLocal: 'SUCURSAL', direccion: 'Av. Terciaria 789', isActive: true },
          { id: 4, nombre: 'Macartur 4', tipoLocal: 'SUCURSAL', direccion: 'Av. Cuarta 101', isActive: true },
          { id: 5, nombre: 'Macartur 5', tipoLocal: 'SUCURSAL', direccion: 'Av. Quinta 202', isActive: true }
        ];
        setLocations(mockLocations);
      } catch (error) {
        console.error('Error loading locations:', error);
      }
    };

    loadLocations();
  }, []);

  useEffect(() => {
    if (promotion) {
      setFormData({
        name: promotion.name,
        description: promotion.description,
        type: promotion.type,
        value: promotion.value || 0,
        applicableProducts: promotion.applicableProducts || [],
        applicableLocations: promotion.applicableLocations || [],
        startDate: promotion.startDate || '',
        endDate: promotion.endDate || '',
        startTime: promotion.startTime || '',
        endTime: promotion.endTime || '',
        daysOfWeek: promotion.daysOfWeek || []
      });

      // Load selected products and locations
      setSelectedProducts(promotion.applicableProducts || []);
      setSelectedLocations(promotion.applicableLocations || []);
    } else {
      // Reset form when no promotion is provided (creating new)
      setFormData({
        name: '',
        description: '',
        type: 'percentage',
        value: 0,
        applicableProducts: [],
        applicableLocations: [],
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        daysOfWeek: []
      });
      setSelectedProducts([]);
      setSelectedLocations([]);
      setCurrentStep(1);
    }
  }, [promotion]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const finalFormData = {
      ...formData,
      applicableProducts: selectedProducts,
      applicableLocations: selectedLocations
    };

    onSubmit(finalFormData);
  };

  const handleTypeChange = (type: PromotionType) => {
    setFormData(prev => ({
      ...prev,
      type,
      value: 0
    }));
  };

  const handleProductToggle = useCallback((productId: string) => {
    setSelectedProducts(prev => {
      const isSelected = prev.includes(productId);
      return isSelected
        ? prev.filter(id => id !== productId)
        : [...prev, productId];
    });
  }, []);

  const handleLocationToggle = useCallback((locationId: number) => {
    setSelectedLocations(prev => {
      const isSelected = prev.includes(locationId);
      return isSelected
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId];
    });
  }, []);

  const getSelectedProductsInfo = () => {
    return mockProducts.filter(product => selectedProducts.includes(product.id));
  };

  const canProceedToNextStep = (step: number) => {
    switch (step) {
      case 1:
        return formData.name && formData.description && formData.type;
      case 2:
        return formData.value > 0;
      case 3:
        return formData.startDate && formData.endDate;
      case 4:
        return true; // Último paso, siempre puede proceder al submit
      default:
        return true;
    }
  };

  const handleNextStep = () => {
    if (canProceedToNextStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Mostrar mensaje de error específico
      let errorMessage = '';
      switch (currentStep) {
        case 1:
          errorMessage = 'Por favor completa el nombre, descripción y tipo de promoción';
          break;
        case 2:
          errorMessage = 'Por favor configura el valor de la promoción';
          break;
        case 3:
          errorMessage = 'Por favor selecciona las fechas de inicio y fin';
          break;
      }
      alert(errorMessage); // En una aplicación real, usarías un toast o modal
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Progress Indicator */}
      <div className="flex items-center justify-center mb-6 px-4">
        <div className="flex items-center space-x-2 md:space-x-4 max-w-full overflow-x-auto">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="flex items-center flex-shrink-0">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step <= currentStep
                    ? 'bg-macartur-primary text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step}
              </div>
              <span
                className={`ml-1 md:ml-2 text-xs md:text-sm whitespace-nowrap ${
                  step <= currentStep ? 'text-macartur-primary font-medium' : 'text-gray-500'
                }`}
              >
                {step === 1 && 'Info'}
                {step === 2 && 'Productos'}
                {step === 3 && 'Fechas'}
                {step === 4 && 'Locales'}
              </span>
              {step < 4 && (
                <div
                  className={`w-6 md:w-12 h-0.5 ml-2 md:ml-4 ${
                    step < currentStep ? 'bg-macartur-primary' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Paso 1: Información Básica */}
      {currentStep === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Información Básica</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre de la Promoción</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Descuento 20% en Sándwiches"
              />
            </div>
            
            <div>
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe los detalles de la promoción"
              />
            </div>
            
            <div>
              <Label htmlFor="type">Tipo de Promoción</Label>
              <Select value={formData.type} onValueChange={handleTypeChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Descuento Porcentual</SelectItem>
                  <SelectItem value="fixed">Descuento Fijo</SelectItem>
                  <SelectItem value="combo">Combo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Paso 2: Selección de Productos */}
      {currentStep === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Productos Aplicables</CardTitle>
            <p className="text-sm text-gray-600">
              Selecciona los productos a los que se aplicará esta promoción
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Buscador de productos */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Buscar productos..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Lista de productos */}
            <div className="grid grid-cols-1 gap-3 max-h-64 overflow-y-auto">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedProducts.includes(product.id)
                      ? 'border-macartur-primary bg-macartur-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleProductToggle(product.id)}
                >
                  <div className="flex items-center space-x-3">
                    <div 
                      className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                        selectedProducts.includes(product.id)
                          ? 'bg-macartur-primary border-macartur-primary'
                          : 'border-gray-300'
                      }`}
                    >
                      {selectedProducts.includes(product.id) && (
                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">{product.name}</h4>
                      <div className="flex justify-between items-center">
                        <p className="text-sm text-gray-500">{product.category}</p>
                        <p className="text-sm font-medium text-macartur-primary">S/ {product.price.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Resumen de productos seleccionados */}
            {selectedProducts.length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">
                  Productos Seleccionados ({selectedProducts.length})
                </h4>
                <div className="space-y-2">
                  {getSelectedProductsInfo().map((product) => (
                    <div key={product.id} className="flex justify-between items-center text-sm">
                      <span>{product.name}</span>
                      <span className="font-medium">S/ {product.price.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                
                {/* Preview del descuento */}
                {formData.value > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Preview del Descuento:</h5>
                    <div className="space-y-1">
                      {formData.type === 'combo' ? (
                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-2">Combo incluye:</div>
                          {getSelectedProductsInfo().map((product) => (
                            <div key={product.id} className="flex justify-between items-center text-sm pl-2">
                              <span className="truncate flex-1 pr-2">• {product.name}</span>
                              <span className="line-through text-gray-400 text-xs">S/ {product.price.toFixed(2)}</span>
                            </div>
                          ))}
                          <div className="border-t border-gray-200 mt-2 pt-2">
                            <div className="flex justify-between items-center text-sm font-semibold">
                              <span>Precio Total Individual:</span>
                              <span className="line-through text-gray-400">
                                S/ {getSelectedProductsInfo().reduce((sum, p) => sum + p.price, 0).toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center text-lg font-bold text-green-600">
                              <span>Precio del Combo:</span>
                              <span>S/ {(formData.value || 0).toFixed(2)}</span>
                            </div>
                            <div className="text-right text-sm text-green-600 font-medium">
                              Ahorro: S/ {Math.max(0, getSelectedProductsInfo().reduce((sum, p) => sum + p.price, 0) - (formData.value || 0)).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      ) : (
                        (() => {
                          const totalOriginal = getSelectedProductsInfo().reduce((sum, p) => sum + p.price, 0);
                          const totalDiscount = formData.type === 'percentage'
                            ? (totalOriginal * (formData.value || 0)) / 100
                            : formData.value || 0;
                          const finalTotal = Math.max(0, totalOriginal - totalDiscount);
                          
                          return (
                            <div>
                              <div className="text-sm font-medium text-gray-700 mb-2">Productos incluidos:</div>
                              {getSelectedProductsInfo().map((product) => (
                                <div key={product.id} className="flex justify-between items-center text-sm pl-2">
                                  <span className="truncate flex-1 pr-2">• {product.name}</span>
                                  <span className="font-medium">S/ {product.price.toFixed(2)}</span>
                                </div>
                              ))}
                              <div className="border-t border-gray-200 mt-2 pt-2">
                                <div className="flex justify-between items-center text-sm font-semibold">
                                  <span>Total Original:</span>
                                  <span className="line-through text-gray-400">S/ {totalOriginal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm text-red-600">
                                  <span>Descuento {formData.type === 'percentage' ? `(${formData.value}%)` : 'Fijo'}:</span>
                                  <span>- S/ {totalDiscount.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between items-center text-lg font-bold text-green-600">
                                  <span>Total Final:</span>
                                  <span>S/ {finalTotal.toFixed(2)}</span>
                                </div>
                                <div className="text-right text-sm text-green-600 font-medium">
                                  Ahorro: S/ {totalDiscount.toFixed(2)}
                                </div>
                              </div>
                            </div>
                          );
                        })()
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Configuración del Descuento */}
            {getSelectedProductsInfo().length > 0 && (
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-3">Configurar Descuento</h4>
                <div className="grid grid-cols-1 gap-4">
                  {formData.type === 'percentage' && (
                    <div>
                      <Label htmlFor="value">Porcentaje de Descuento (%)</Label>
                      <Input
                        id="value"
                        type="number"
                        min="0"
                        max="100"
                        value={formData.value}
                        onChange={(e) => setFormData(prev => ({ ...prev, value: Number(e.target.value) }))}
                        placeholder="Ej: 20"
                      />
                    </div>
                  )}

                  {formData.type === 'fixed' && (
                    <div>
                      <Label htmlFor="value">Monto de Descuento (S/)</Label>
                      <Input
                        id="value"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.value}
                        onChange={(e) => setFormData(prev => ({ ...prev, value: Number(e.target.value) }))}
                        placeholder="Ej: 5.00"
                      />
                    </div>
                  )}

                  {formData.type === 'combo' && (
                    <div>
                      <Label htmlFor="value">Precio del Combo (S/)</Label>
                      <Input
                        id="value"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.value}
                        onChange={(e) => setFormData(prev => ({ ...prev, value: Number(e.target.value) }))}
                        placeholder="Ej: 25.00"
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Precio fijo que pagará el cliente por todos los productos seleccionados
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Paso 3: Programación */}
      {currentStep === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>Programación</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Fechas */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Período de Validez</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startDate">Fecha de Inicio</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      startDate: e.target.value
                    }))}
                  />
                </div>
                
                <div>
                  <Label htmlFor="endDate">Fecha de Fin</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      endDate: e.target.value
                    }))}
                  />
                </div>
              </div>
            </div>

            {/* Horarios */}
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Horario de Activación</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startTime">Hora de Inicio</Label>
                  <Input
                    id="startTime"
                    type="time"
                    value={formData.startTime || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      startTime: e.target.value
                    }))}
                    placeholder="Ej: 14:00"
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Opcional: Deja vacío para activar todo el día
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="endTime">Hora de Fin</Label>
                  <Input
                    id="endTime"
                    type="time"
                    value={formData.endTime || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      endTime: e.target.value
                    }))}
                    placeholder="Ej: 17:00"
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Ideal para promociones en "horas muertas"
                  </p>
                </div>
              </div>
            </div>

            {/* Días de la Semana */}
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Días Permitidos</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                {[
                  { value: 0, label: 'Dom', fullName: 'Domingo' },
                  { value: 1, label: 'Lun', fullName: 'Lunes' },
                  { value: 2, label: 'Mar', fullName: 'Martes' },
                  { value: 3, label: 'Mié', fullName: 'Miércoles' },
                  { value: 4, label: 'Jue', fullName: 'Jueves' },
                  { value: 5, label: 'Vie', fullName: 'Viernes' },
                  { value: 6, label: 'Sáb', fullName: 'Sábado' }
                ].map((day) => (
                  <label
                    key={day.value}
                    className={`flex flex-col items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                      formData.daysOfWeek?.includes(day.value)
                        ? 'border-macartur-primary bg-rose-50 text-macartur-primary'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    title={day.fullName}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={formData.daysOfWeek?.includes(day.value) || false}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        const currentDays = formData.daysOfWeek || [];

                        setFormData(prev => ({
                          ...prev,
                          daysOfWeek: isChecked
                            ? [...currentDays, day.value]
                            : currentDays.filter(d => d !== day.value)
                        }));
                      }}
                    />
                    <span className="text-sm font-medium">{day.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-600 mt-2">
                Selecciona los días en los que la promoción estará activa. Si no seleccionas ningún día, 
                estará activa todos los días de la semana.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Paso 4: Selección de Locales */}
      {currentStep === 4 && (
        <Card>
          <CardHeader>
            <CardTitle>Locales Aplicables</CardTitle>
            <p className="text-sm text-gray-600">
              Selecciona los locales donde esta promoción estará disponible
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Lista de locales */}
            <div className="grid grid-cols-1 gap-3">
              {locations.map((location) => (
                <div
                  key={location.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedLocations.includes(location.id)
                      ? 'border-macartur-primary bg-macartur-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleLocationToggle(location.id)}
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                        selectedLocations.includes(location.id)
                          ? 'bg-macartur-primary border-macartur-primary'
                          : 'border-gray-300'
                      }`}
                    >
                      {selectedLocations.includes(location.id) && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-900">{location.nombre}</h4>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          location.tipoLocal === 'PRINCIPAL'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {location.tipoLocal}
                        </span>
                      </div>
                      {location.direccion && (
                        <p className="text-sm text-gray-500 mt-1">{location.direccion}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Botón para seleccionar todos */}
            <div className="flex gap-2 pt-3 border-t">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSelectedLocations(locations.map(l => l.id))}
                disabled={selectedLocations.length === locations.length}
              >
                Seleccionar Todos
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSelectedLocations([])}
                disabled={selectedLocations.length === 0}
              >
                Limpiar Selección
              </Button>
            </div>

            {/* Resumen de locales seleccionados */}
            {selectedLocations.length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">
                  Locales Seleccionados ({selectedLocations.length} de {locations.length})
                </h4>
                <div className="space-y-2">
                  {locations
                    .filter(location => selectedLocations.includes(location.id))
                    .map((location) => (
                      <div key={location.id} className="flex justify-between items-center text-sm">
                        <span className="flex items-center gap-2">
                          <span>{location.nombre}</span>
                          <span className={`px-1.5 py-0.5 text-xs rounded ${
                            location.tipoLocal === 'PRINCIPAL'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {location.tipoLocal}
                          </span>
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {selectedLocations.length === 0 && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  ⚠️ Si no seleccionas ningún local, la promoción estará disponible en todos los locales.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <div>
          {currentStep > 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setCurrentStep(prev => prev - 1)}
            >
              Anterior
            </Button>
          )}
        </div>
        
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          
          {currentStep < 4 ? (
            <Button
              type="button"
              onClick={handleNextStep}
              className="bg-macartur-primary hover:bg-macartur-accent1"
            >
              Siguiente
            </Button>
          ) : (
            <Button
              type="submit"
              className="bg-macartur-primary hover:bg-macartur-accent1"
              disabled={loading}
            >
              {loading ? 'Guardando...' : `${promotion ? 'Actualizar' : 'Crear'} Promoción`}
            </Button>
          )}
        </div>
      </div>
    </form>
  );
};